import {
  type LayerSchema,
  type ProjectSchemaJSON,
  type PropertyRefItem,
  parsePropertyRef,
  parseRef,
  type QueryJSON,
  type RefItem,
  refItemToNamespacedShapeName,
  type ServiceConfigJSON,
  type ShapeJSON
} from '@takeshape/schema';
import type { Maybe } from '@takeshape/util';
import type { ProjectSchemaCompat } from 'app/compat/types';
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { type FetchSchemaMode, fetchSchemaRoutine } from '../sagas/fetch-schema';
import { postSchemaRoutine } from '../sagas/post-schema';
import { getBranchByName } from '../selectors';
import type { Branch, BranchesSlice, Layers } from '../types';

type ResolvedRef<Resolved, Ref> = {
  value: Resolved;
  service?: ServiceConfigJSON;
  refItem: Ref;
};

export type ResolveShapeRef = (ref: string) => Maybe<ResolvedRef<ShapeJSON, RefItem>>;
export type ResolveQueryRef = (ref: string) => Maybe<ResolvedRef<QueryJSON, PropertyRefItem>>;

export type UseBranch = {
  branch: Branch;
  resolveShapeRef: ResolveShapeRef;
  resolveQueryRef: ResolveQueryRef;
  fetchSchema: (params: { runtime?: boolean; mode?: FetchSchemaMode }) => void;
  updateSchema: (newSchema: ProjectSchemaCompat, delay?: number) => void;
  getSchemaForService: (serviceId: string) => LayerSchema | undefined;
};

export function getResolveShapeRef(userSchema: ProjectSchemaJSON | undefined | null, layers: Layers): ResolveShapeRef {
  return (refStr) => {
    if (userSchema) {
      const ref = parseRef(userSchema, refStr);
      const shapeName = refItemToNamespacedShapeName(userSchema, ref);
      if (ref.layerId === 'local') {
        const shape = userSchema.shapes[shapeName];
        if (shape) {
          return { value: shape, refItem: ref };
        }
      }

      const shape = layers[ref.layerId]?.data?.shapes[shapeName];
      if (shape) {
        return {
          value: shape,
          service: userSchema.services?.[ref.layerId],
          refItem: ref
        };
      }
    }
  };
}

export function getResolveQueryRef(userSchema: ProjectSchemaJSON | undefined | null, layers: Layers): ResolveQueryRef {
  return (refStr) => {
    if (userSchema) {
      const ref = parsePropertyRef(refStr);
      if (ref && (ref.shapeName === 'Query' || ref.shapeName === 'Mutation')) {
        const key = ref.shapeName === 'Query' ? 'queries' : 'mutations';
        if (ref.layerId === 'local') {
          const query = userSchema[key][ref.propertyName];
          if (query) {
            return { value: query, refItem: ref };
          }
        }

        const query = layers[ref.layerId]?.data?.[key][ref.propertyName];
        if (query) {
          return {
            value: query,
            service: userSchema.services?.[ref.layerId],
            refItem: ref
          };
        }
      }
    }
  };
}

/** Returns the named branch, along with functions to fetch or update the branch's schemas. */
export function useBranch(name: string): UseBranch {
  const dispatch = useDispatch();

  const branch: Branch | undefined = useSelector<BranchesSlice, Branch | undefined>((state) =>
    getBranchByName(state, name)
  );

  if (!branch) {
    throw new Error(`Branch "${name}" does not exist.`);
  }

  /** Triggers a GET request for the branch's schema. Specify whether you want the runtime schema when calling.
   *  While it is fetching, the layer or runtime will be marked "loading".
   *  After it is fetched, the schema will be available in the selected `branch`.
   */
  const fetchSchema = useCallback(
    (params: { runtime?: boolean; mode?: FetchSchemaMode }) => {
      dispatch(
        fetchSchemaRoutine.requested(undefined, {
          branch: name,
          runtime: params.runtime,
          mode: params.mode
        })
      );
    },
    [dispatch, name]
  );

  /** Triggers a POST request on the branch's schema. Will always apply the update to the runtime schema.
   *  While it is updating, the runtime will be marked "saving".
   *  Afer it is done updating, the runtime will be updated with the newly returned server value.
   */
  const updateSchema = useCallback(
    (newSchema: ProjectSchemaCompat, delay?: number) => {
      dispatch(
        postSchemaRoutine.requested(newSchema, {
          branch: name,
          runtime: true,
          delay
        })
      );
    },
    [dispatch, name]
  );

  /**
   * Get service schema for a given service
   */
  const getSchemaForService = useCallback(
    (serviceId: string) => branch.layers[serviceId]?.data ?? undefined,
    [branch.layers]
  );

  const resolveShapeRef = useMemo(() => getResolveShapeRef(branch.layers.user.data, branch.layers), [branch.layers]);
  const resolveQueryRef = useMemo(() => getResolveQueryRef(branch.layers.user.data, branch.layers), [branch.layers]);

  return {
    branch,
    resolveShapeRef,
    resolveQueryRef,
    fetchSchema,
    updateSchema,
    getSchemaForService
  };
}
